import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import Alert from '../components/Alert';
import { useForm } from '../hooks/useForm';

const Register = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { register } = useAuth();
    const [searchParams] = useSearchParams();
    const { formData, handleChange: handleFormChange } = useForm({
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        confirmPassword: '',
        organizationName: '',
        licenseNumber: '',
    });
    const [invitationToken, setInvitationToken] = useState('');
    const [isInvitation, setIsInvitation] = useState(false);
    const [invitationDetails, setInvitationDetails] = useState(null);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [selectedPlan, setSelectedPlan] = useState('free');
    const [billingPeriod, setBillingPeriod] = useState('monthly');
    const [paymentData, setPaymentData] = useState({
        cardNumber: '',
        cardName: '',
        expiryDate: '',
        cvv: '',
    });

    // Plan pricing
    const planPrices = {
        free: { monthly: 0, yearly: 0 },
        basic: { monthly: 49, yearly: 490 },
        pro: { monthly: 79, yearly: 790 },
    };

    useEffect(() => {
        const planParam = searchParams.get('plan');
        if (planParam && ['free', 'basic', 'pro'].includes(planParam)) {
            setSelectedPlan(planParam);
        }

        const invitationParam = searchParams.get('invitation');
        if (invitationParam) {
            setInvitationToken(invitationParam);
            setIsInvitation(true);
            setSelectedPlan('free'); // plan is managed by the org admin

            // Fetch invitation details to display org name and assigned plan
            axios.get(`/api/organizations/invitation/${invitationParam}`)
                .then(res => setInvitationDetails(res.data))
                .catch(() => { }); // invalid token will be caught on submit
        }
    }, [searchParams]);

    const handlePaymentChange = (e) => {
        setPaymentData({
            ...paymentData,
            [e.target.name]: e.target.value,
        });
    };

    const handleGoogleSignup = () => {
        window.location.href = `${process.env.REACT_APP_API_URL || 'http://localhost:5001'}/api/auth/google`;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        // Validation
        if (formData.password !== formData.confirmPassword) {
            setError(t('validation.passwordMismatch'));
            return;
        }

        if (formData.password.length < 8) {
            setError(t('validation.passwordTooShort'));
            return;
        }

        // Payment validation for paid plans
        if (selectedPlan !== 'free') {
            if (!paymentData.cardNumber || !paymentData.cardName || !paymentData.expiryDate || !paymentData.cvv) {
                setError(t('registerPage.errors.paymentRequired'));
                return;
            }
            // Basic card number validation
            if (paymentData.cardNumber.replace(/\s/g, '').length < 13) {
                setError(t('registerPage.errors.cardInvalid'));
                return;
            }
        }

        setLoading(true);

        // In a real application, you would process payment here
        // For now, we'll just include the subscription info
        const result = await register({
            firstName: formData.firstName,
            lastName: formData.lastName,
            email: formData.email,
            password: formData.password,
            organizationName: formData.organizationName,
            licenseNumber: formData.licenseNumber,
            invitationToken: invitationToken || undefined,
            subscription: {
                plan: selectedPlan,
                billingPeriod: billingPeriod,
                amount: planPrices[selectedPlan][billingPeriod],
            },
        });

        setLoading(false);

        if (result.success) {
            navigate('/dashboard');
        } else {
            setError(result.message);
        }
    };

    return (
        <div className="page-transition min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-md w-full">
                <div className="text-center mb-8">
                    <h1 className="text-3xl font-bold text-gray-900 mb-2">{t('auth.register.title')}</h1>
                    <p className="text-gray-600">
                        {t('registerPage.subtitle')}
                    </p>
                </div>

                <div className="card">
                    <Alert type="error" message={error} onDismiss={() => setError('')} />

                    <form onSubmit={handleSubmit} className="space-y-6">
                        {/* Subscription Plan Selection - hidden for invited users */}
                        {!isInvitation && (<div className="border-b pb-6">
                            <h2 className="text-xl font-semibold mb-4">{t('registerPage.plan.title')}</h2>
                            <div className="grid grid-cols-3 gap-3">
                                <button
                                    type="button"
                                    onClick={() => setSelectedPlan('free')}
                                    className={`p-4 rounded-lg border-2 transition-all min-h-touch ${selectedPlan === 'free'
                                        ? 'border-green-500 bg-green-50'
                                        : 'border-gray-200 hover:border-green-300 active:bg-green-50'
                                        }`}
                                >
                                    <div className="text-sm font-semibold text-green-600 mb-1">{t('registerPage.plan.freeBadge')}</div>
                                    <div className="text-2xl font-bold">€0</div>
                                    <div className="text-xs text-gray-600 mt-1">{t('registerPage.plan.freeDuration')}</div>
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setSelectedPlan('basic')}
                                    className={`p-4 rounded-lg border-2 transition-all min-h-touch ${selectedPlan === 'basic'
                                        ? 'border-blue-500 bg-blue-50'
                                        : 'border-gray-200 hover:border-blue-300 active:bg-blue-50'
                                        }`}
                                >
                                    <div className="text-sm font-semibold text-blue-600 mb-1">{t('registerPage.plan.basicBadge')}</div>
                                    <div className="text-2xl font-bold">€{billingPeriod === 'monthly' ? '49' : '490'}</div>
                                    <div className="text-xs text-gray-600 mt-1">{billingPeriod === 'monthly' ? t('registerPage.plan.perMonth') : t('registerPage.plan.perYear')}</div>
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setSelectedPlan('pro')}
                                    className={`p-4 rounded-lg border-2 transition-all min-h-touch ${selectedPlan === 'pro'
                                        ? 'border-purple-500 bg-purple-50'
                                        : 'border-gray-200 hover:border-purple-300 active:bg-purple-50'
                                        }`}
                                >
                                    <div className="text-sm font-semibold text-purple-600 mb-1">{t('registerPage.plan.proBadge')}</div>
                                    <div className="text-2xl font-bold">€{billingPeriod === 'monthly' ? '79' : '790'}</div>
                                    <div className="text-xs text-gray-600 mt-1">{billingPeriod === 'monthly' ? t('registerPage.plan.perMonth') : t('registerPage.plan.perYear')}</div>
                                </button>
                            </div>

                            {selectedPlan !== 'free' && (
                                <div className="mt-4">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        {t('registerPage.plan.billingPeriod')}
                                    </label>
                                    <div className="flex gap-3">
                                        <button
                                            type="button"
                                            onClick={() => setBillingPeriod('monthly')}
                                            className={`flex-1 px-4 py-3 rounded-lg border-2 transition-all min-h-touch ${billingPeriod === 'monthly'
                                                ? 'border-blue-500 bg-blue-50 text-blue-700 font-semibold'
                                                : 'border-gray-200 hover:border-blue-300 active:bg-blue-50'
                                                }`}
                                        >
                                            {t('profile.subscription.monthly')}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setBillingPeriod('yearly')}
                                            className={`flex-1 px-4 py-3 rounded-lg border-2 transition-all min-h-touch ${billingPeriod === 'yearly'
                                                ? 'border-blue-500 bg-blue-50 text-blue-700 font-semibold'
                                                : 'border-gray-200 hover:border-blue-300 active:bg-blue-50'
                                                }`}
                                        >
                                            {t('profile.subscription.yearly')} <span className="text-green-600 font-semibold">{t('registerPage.plan.saveTwoMonths')}</span>
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>)}

                        {/* Account Information */}
                        <div className="border-b pb-6">
                            <h2 className="text-xl font-semibold mb-4">{t('registerPage.accountInfoTitle')}</h2>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1">
                                        {t('auth.register.firstName')} *
                                    </label>
                                    <input
                                        type="text"
                                        id="firstName"
                                        name="firstName"
                                        required
                                        value={formData.firstName}
                                        onChange={handleFormChange}
                                        className="input-field"
                                        placeholder={t('registerPage.placeholders.firstName')}
                                    />
                                </div>
                                <div>
                                    <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1">
                                        {t('auth.register.lastName')} *
                                    </label>
                                    <input
                                        type="text"
                                        id="lastName"
                                        name="lastName"
                                        required
                                        value={formData.lastName}
                                        onChange={handleFormChange}
                                        className="input-field"
                                        placeholder={t('registerPage.placeholders.lastName')}
                                    />
                                </div>
                            </div>

                            <div>
                                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                                    {t('auth.register.email')} *
                                </label>
                                <input
                                    type="email"
                                    id="email"
                                    name="email"
                                    required
                                    value={formData.email}
                                    onChange={handleFormChange}
                                    className="input-field"
                                    placeholder={t('registerPage.placeholders.email')}
                                    autoComplete="email"
                                />
                            </div>

                            {!isInvitation && (
                                <div>
                                    <label htmlFor="organizationName" className="block text-sm font-medium text-gray-700 mb-1">
                                        {t('auth.register.organization')} *
                                    </label>
                                    <input
                                        type="text"
                                        id="organizationName"
                                        name="organizationName"
                                        required={!isInvitation}
                                        value={formData.organizationName}
                                        onChange={handleFormChange}
                                        className="input-field"
                                        placeholder={t('registerPage.placeholders.organization')}
                                    />
                                    <p className="text-xs text-gray-500 mt-1">
                                        {t('registerPage.organizationAdminHint')}
                                    </p>
                                </div>
                            )}

                            {isInvitation && (
                                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-1">
                                    <p className="text-sm font-semibold text-blue-800">{t('registerPage.invitation.joiningTitle')}</p>
                                    {invitationDetails ? (
                                        <>
                                            <p className="text-sm text-blue-800">
                                                {t('registerPage.invitation.organization')}: <strong>{invitationDetails.organizationName}</strong>
                                            </p>
                                            <p className="text-sm text-blue-800">
                                                {t('registerPage.invitation.plan')}: <strong>{invitationDetails.subscription}</strong>
                                                {' · '}
                                                <span className="capitalize">{invitationDetails.billingPeriod}</span>
                                            </p>
                                            <p className="text-xs text-blue-700">
                                                {t('registerPage.invitation.managedByAdmin')}
                                            </p>
                                        </>
                                    ) : (
                                        <p className="text-xs text-blue-700">
                                            {t('registerPage.invitation.creatingToJoin')}
                                        </p>
                                    )}
                                </div>
                            )}

                            <div>
                                <label htmlFor="licenseNumber" className="block text-sm font-medium text-gray-700 mb-1">
                                    {t('profile.information.licenseNumber')}
                                </label>
                                <input
                                    type="text"
                                    id="licenseNumber"
                                    name="licenseNumber"
                                    value={formData.licenseNumber}
                                    onChange={handleFormChange}
                                    className="input-field"
                                    placeholder={t('registerPage.placeholders.licenseNumber')}
                                />
                            </div>

                            <div>
                                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                                    {t('auth.register.password')} *
                                </label>
                                <input
                                    type="password"
                                    id="password"
                                    name="password"
                                    required
                                    value={formData.password}
                                    onChange={handleFormChange}
                                    className="input-field"
                                    placeholder={t('registerPage.placeholders.password')}
                                    autoComplete="new-password"
                                />
                            </div>

                            <div>
                                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
                                    {t('auth.register.confirmPassword')} *
                                </label>
                                <input
                                    type="password"
                                    id="confirmPassword"
                                    name="confirmPassword"
                                    required
                                    value={formData.confirmPassword}
                                    onChange={handleFormChange}
                                    className="input-field"
                                    placeholder={t('registerPage.placeholders.confirmPassword')}
                                    autoComplete="new-password"
                                />
                            </div>
                        </div>

                        {/* Payment Information - Only for paid plans & new org owners */}
                        {selectedPlan !== 'free' && !isInvitation && (
                            <div className="border-b pb-6">
                                <h2 className="text-xl font-semibold mb-4">{t('registerPage.payment.title')}</h2>
                                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                                    <p className="text-sm font-semibold text-blue-800">
                                        {t('registerPage.payment.total')}: €{planPrices[selectedPlan][billingPeriod]} {billingPeriod === 'monthly' ? t('registerPage.plan.perMonth') : t('registerPage.plan.perYear')}
                                    </p>
                                    {billingPeriod === 'yearly' && (
                                        <p className="text-xs text-blue-700 mt-1">
                                            {t('registerPage.payment.yearlySave', { amount: planPrices[selectedPlan].monthly * 2 })}
                                        </p>
                                    )}
                                </div>

                                <div className="space-y-4">
                                    <div>
                                        <label htmlFor="cardName" className="block text-sm font-medium text-gray-700 mb-1">
                                            {t('organizationSettings.subscription.cardholderName')} *
                                        </label>
                                        <input
                                            type="text"
                                            id="cardName"
                                            name="cardName"
                                            required={selectedPlan !== 'free'}
                                            value={paymentData.cardName}
                                            onChange={handlePaymentChange}
                                            className="input-field"
                                            placeholder={t('registerPage.placeholders.cardName')}
                                        />
                                    </div>

                                    <div>
                                        <label htmlFor="cardNumber" className="block text-sm font-medium text-gray-700 mb-1">
                                            {t('organizationSettings.subscription.cardNumber')} *
                                        </label>
                                        <input
                                            type="text"
                                            id="cardNumber"
                                            name="cardNumber"
                                            required={selectedPlan !== 'free'}
                                            value={paymentData.cardNumber}
                                            onChange={handlePaymentChange}
                                            className="input-field"
                                            placeholder={t('registerPage.placeholders.cardNumber')}
                                            maxLength="19"
                                        />
                                    </div>

                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label htmlFor="expiryDate" className="block text-sm font-medium text-gray-700 mb-1">
                                                {t('organizationSettings.subscription.expiryDate')} *
                                            </label>
                                            <input
                                                type="text"
                                                id="expiryDate"
                                                name="expiryDate"
                                                required={selectedPlan !== 'free'}
                                                value={paymentData.expiryDate}
                                                onChange={handlePaymentChange}
                                                className="input-field"
                                                placeholder={t('registerPage.placeholders.expiryDate')}
                                                maxLength="5"
                                            />
                                        </div>
                                        <div>
                                            <label htmlFor="cvv" className="block text-sm font-medium text-gray-700 mb-1">
                                                {t('organizationSettings.subscription.cvv')} *
                                            </label>
                                            <input
                                                type="text"
                                                id="cvv"
                                                name="cvv"
                                                required={selectedPlan !== 'free'}
                                                value={paymentData.cvv}
                                                onChange={handlePaymentChange}
                                                className="input-field"
                                                placeholder={t('registerPage.placeholders.cvv')}
                                                maxLength="4"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {selectedPlan === 'free' && !isInvitation && (
                            <div className="p-4 bg-blue-50 rounded-lg">
                                <p className="text-sm text-blue-800">
                                    <strong>{t('registerPage.freeTrial.includes')}:</strong>
                                </p>
                                <ul className="text-sm text-blue-700 mt-2 space-y-1 ml-4 list-disc">
                                    <li>{t('registerPage.freeTrial.item1')}</li>
                                    <li>{t('registerPage.freeTrial.item2')}</li>
                                    <li>{t('registerPage.freeTrial.item3')}</li>
                                    <li>{t('registerPage.freeTrial.item4')}</li>
                                </ul>
                            </div>
                        )}

                        <button
                            type="submit"
                            disabled={loading}
                            className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {loading
                                ? t('registerPage.processing')
                                : selectedPlan === 'free'
                                    ? t('auth.register.submitButton')
                                    : t('registerPage.createAndSubscribe')
                            }
                        </button>

                        <div className="mt-6">
                            <div className="relative">
                                <div className="absolute inset-0 flex items-center">
                                    <div className="w-full border-t border-gray-300"></div>
                                </div>
                                <div className="relative flex justify-center text-sm">
                                    <span className="px-2 bg-white text-gray-500">{t('auth.register.divider')}</span>
                                </div>
                            </div>

                            <button
                                type="button"
                                onClick={handleGoogleSignup}
                                className="mt-4 w-full flex items-center justify-center gap-3 bg-white border-2 border-gray-300 text-gray-700 px-6 py-3 rounded-lg font-semibold hover:bg-gray-50 transition-colors"
                            >
                                <svg className="w-5 h-5" viewBox="0 0 24 24">
                                    <path
                                        fill="#4285F4"
                                        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                                    />
                                    <path
                                        fill="#34A853"
                                        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                                    />
                                    <path
                                        fill="#FBBC05"
                                        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                                    />
                                    <path
                                        fill="#EA4335"
                                        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                                    />
                                </svg>
                                {t('auth.register.googleButton')}
                            </button>
                        </div>
                    </form>

                    <div className="mt-6 text-center">
                        <p className="text-sm text-gray-600">
                            {t('auth.register.hasAccount')}{' '}
                            <Link to="/login" className="text-blue-600 hover:text-blue-700 font-semibold">
                                {t('auth.login.submitButton')}
                            </Link>
                        </p>
                    </div>
                </div>
            </div >
        </div >
    );
};

export default Register;
